import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiChevronLeft } from "react-icons/fi";
import { onAuthStateChanged } from "firebase/auth";

import UserMenuButton from "../../components/header/UserMenuButton";
import FavoritesButton from "../../components/header/FavoritesButton";
import MainMenuButton from "../../components/header/MainMenuButton";
import logo from "../../assets/konteyner_logo.png";

import { auth } from "../../firebase";
import { apiGetFavorites, apiRemoveFavorite } from "../../api/favorites.api";

import "../../styles/InfoLayout.css";

export default function InfoLayout({ chatId = "", title = "ABOUT", children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [openMenu, setOpenMenu] = useState(null);

  const [favoriteItems, setFavoriteItems] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [authReady, setAuthReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setOpenMenu(null);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
      setAuthReady(true);
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    if (!authReady) return;

    let ignore = false;

    async function loadFavorites() {
      if (!isLoggedIn) {
        setFavoriteIds([]);
        setFavoriteItems([]);
        return;
      }

      try {
        const res = await apiGetFavorites();
        if (ignore) return;

        if (res?.unauthenticated) {
          setFavoriteIds([]);
          setFavoriteItems([]);
          return;
        }

        setFavoriteIds(Array.isArray(res?.ids) ? res.ids : []);
        setFavoriteItems(Array.isArray(res?.items) ? res.items : []);
      } catch (e) {
        console.error("InfoLayout favorites load error:", e);
        if (!ignore) {
          setFavoriteIds([]);
          setFavoriteItems([]);
        }
      }
    }

    loadFavorites();

    return () => {
      ignore = true;
    };
  }, [authReady, isLoggedIn]);

  function productKey(p) {
    return String(p?.product_id || p?.variant_id || p?.id || "");
  }

  async function handleRemoveFavorite(product) {
    const key = productKey(product);
    if (!key) return;

    try {
      const res = await apiRemoveFavorite(product);

      if (res?.unauthenticated) {
        return;
      }

      setFavoriteIds((prev) => prev.filter((id) => String(id) !== String(key)));
      setFavoriteItems((prev) =>
        prev.filter((p) => String(productKey(p)) !== String(key))
      );
    } catch (e) {
      console.error("InfoLayout remove favorite error:", e);
    }
  }

  function handleOpenProduct(product) {
    navigate("/", {
      state: {
        openProduct: product,
        fromChatId: chatId || "",
      },
    });
    setOpenMenu(null);
  }

  function goBack() {
  const fromChatId = location.state?.fromChatId;
  const fromOrders = location.state?.fromOrders;
  const reopenMainMenuOnBack = location.state?.reopenMainMenuOnBack;
  const reopenUserMenuOnBack = location.state?.reopenUserMenuOnBack;

  if (fromOrders) {
    navigate("/orders", {
      state: {
        fromChatId,
        reopenMainMenuOnBack,
        reopenUserMenuOnBack,
      },
    });
    return;
  }

  if (fromChatId) {
  navigate(`/chat/${fromChatId}`, {
    state: {
      reopenMainMenu: !!reopenMainMenuOnBack,
      reopenUserMenu: !!reopenUserMenuOnBack,
    },
  });
  return;
}

  navigate("/", {
    state: {
      reopenMainMenu: !!reopenMainMenuOnBack,
      reopenUserMenu: !!reopenUserMenuOnBack,
    },
  });
}

  return (
    <div className="infoPage">
      <header className="topbar">
        <div className="topbarLeft">
          <UserMenuButton
            chatId={chatId}
            open={openMenu === "user"}
            onToggle={() => setOpenMenu((v) => (v === "user" ? null : "user"))}
            onClose={() => setOpenMenu(null)}
            onOpenChat={(id) => {
              navigate("/", { state: { openChatId: id } });
              setOpenMenu(null);
            }}
            onNewChat={() => {
              navigate("/", { state: { openChatId: null, newChat: true } });
              setOpenMenu(null);
            }}
          />
        </div>

        <div className="topbarCenter" aria-label="Лого">
          <img src={logo} alt="ChatShop" className="brandLogoImg" />
        </div>

        <div className="topbarRight">
          <FavoritesButton
            open={openMenu === "favorites"}
            isActive={favoriteItems.length > 0}
            onToggle={() =>
              setOpenMenu((v) => (v === "favorites" ? null : "favorites"))
            }
            onClose={() => setOpenMenu(null)}
            items={favoriteItems}
            onOpenProduct={handleOpenProduct}
            onRemoveItem={handleRemoveFavorite}
            topOffset={60}
          />

          <MainMenuButton
            chatId={location.state?.fromChatId || chatId}
            open={openMenu === "main"}
            onToggle={() => setOpenMenu((v) => (v === "main" ? null : "main"))}
            onClose={() => setOpenMenu(null)}
          />
        </div>
      </header>

      <div className="infoSubBar">
        <button className="infoBackBtn" onClick={goBack} aria-label="Назад">
          <FiChevronLeft className="infoBackIcon" />
        </button>

        <div className="infoSubTitle">{title}</div>

        <div className="infoSubStub" />
      </div>

      <main className="infoBody">{children}</main>
    </div>
  );
}